/**
 * ForgotPassword.jsx (iter148) — Public page.
 * Submits email → backend emails a reset link → shows neutral success screen.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Barcode, EnvelopeSimple, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import ThemeToggleButton from '../components/ThemeToggleButton';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (err) {
      // Backend returns neutral 200 even for unknown emails; the only
      // realistic error here is a server/network problem.
      setError(err?.response?.data?.detail || 'Could not send reset email. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative">
      <div className="absolute top-4 right-4 z-10"><ThemeToggleButton /></div>

      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-md bg-brand flex items-center justify-center">
            <Barcode weight="bold" className="text-brand-foreground" size={24} />
          </div>
          <span className="font-bold text-2xl tracking-tight text-foreground">STOCKARIS</span>
        </div>

        <div className="bg-card border border-border rounded-lg p-8 shadow-sm" data-testid="forgot-password-card">
          {!submitted ? (
            <>
              <h1 className="text-2xl font-semibold mb-1 text-foreground tracking-tight">Forgot password</h1>
              <p className="text-muted-foreground mb-6 text-sm">
                Enter your account email and we&apos;ll send a link to reset it. The link is valid for 60 minutes.
              </p>

              {error && (
                <div data-testid="forgot-error" className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="overline">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="forgot-email"
                    className="mt-1"
                    autoFocus
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={loading || !email}
                  className="w-full font-semibold py-3"
                >
                  {loading ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>

              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="forgot-back-to-login"
              >
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <EnvelopeSimple size={24} weight="fill" className="text-emerald-700" />
              </div>
              <h1 className="text-xl font-semibold mb-2">Check your email</h1>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                If an account exists for <b>{email}</b>, we&apos;ve sent a password reset link.
                It&apos;s valid for 60 minutes. Don&apos;t see it? Check your spam folder.
              </p>
              <div className="flex flex-col gap-2">
                <Link to="/login" className="text-sm text-brand font-semibold hover:underline" data-testid="forgot-success-to-login">
                  Back to sign in
                </Link>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => { setSubmitted(false); setEmail(''); }}
                  data-testid="forgot-try-different-email"
                >
                  Try a different email
                </button>
              </div>
              <div className="mt-6 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded">
                <CheckCircle size={12} weight="fill" className="inline mr-1" />
                For security, we always show this message — even if no account exists for that email.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
